import { PhotoModel } from './protocols';
import { BaseType } from './Enums';

export enum IssuePriority {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high'
}

/**
 * Issue source indicates how the issue was created
 * - 'publicReport': Created via public QR code form (anonymous)
 * - 'workOrder': Reported from a work order (Felanmäl, #1650), with `sourceWorkOrderKey`
 * - undefined/missing: Created internally by authenticated users
 */
export type IssueSource = 'publicReport' | 'workOrder';

/**
 * What kind of thing an issue is about when no actual component exists to
 * link (most armaturer, lås, dörrstängare were never registered). Uses the
 * same hierarchy as components so issues can be counted per type together
 * with component-linked ones.
 *
 * `componentKey` and `componentCategory` are mutually exclusive: every writer
 * clears the other in the same update. Web admin only; iOS and the public
 * report never write it. `label` is a display snapshot (like `unitLabel`)
 * for when the type key is later renamed or deleted — readers prefer the
 * live translation and fall back to it.
 */
export interface IssueComponentCategory {
    baseType: BaseType;
    /** Key in config/buildComponentTypes; its definition's baseType equals `baseType`. */
    specificType?: string;
    label: string;
}

/** What the UI sends; the API fills in `label` from the type config. */
export type IssueComponentCategoryInput = Omit<IssueComponentCategory, 'label'>;

/** The link an issue has to the thing it is about: one of these, or nothing. */
export type IssueComponentLink =
    | { componentKey: string }
    | { componentCategory: IssueComponentCategoryInput }
    | null;

/**
 * The förvaltare's judgement of an issue's cost against the auto-assignment
 * threshold (molnfast-react#1653). Written only by the guestApi `assessIssue`
 * action; clients never write it and carry it over unchanged when archiving.
 */
export interface IssueCostAssessment {
    decision: 'below' | 'above';
    /** External user uid (the förvaltare) */
    decidedBy: string;
    decidedAt: string;
    /** kr exkl. moms at decision time */
    threshold: number;
    comment?: string;
}

export interface Issue {
    key: string;
    buildingKey: string;
    componentKey?: string;
    componentCategory?: IssueComponentCategory;
    description: string;
    priority: IssuePriority;
    photos?: { [key: string]: PhotoModel };
    createdBy: string;  // UID or 'public' for anonymous submissions
    createdAt: string;
    /** Source of the issue - 'publicReport' for QR submissions, 'workOrder' from an ärende, undefined for internal */
    source?: IssueSource;
    /** The work order (active or archived) a 'workOrder' issue was reported from */
    sourceWorkOrderKey?: string;
    // Archive fields (set when archived or converted to work order)
    convertedToWorkOrderKey?: string;
    archivedAt?: string;
    archivedBy?: string;
    archiveReason?: string;
    // Snooze fields (admin-only, temporary hide from active list)
    snoozedUntil?: string;  // YYYY-MM-DD format
    snoozedBy?: string;     // UID of admin who snoozed
    snoozedAt?: string;     // YYYY-MM-DD hh:mm:ss format
    /** Set by the förvaltare under automatisk tilldelning (#1653) */
    costAssessment?: IssueCostAssessment;
}

/** Where an issue reported from a work order is now: Ny / Omvandlad till ärende / Arkiverad */
export type ReportedIssueState = 'active' | 'converted' | 'archived';

export interface ReportedIssue {
    issue: Issue;
    state: ReportedIssueState;
}

// For list views with building name resolved
export interface IssueWithBuilding extends Issue {
    buildingName: string;
    componentName?: string;
}
